import { useEffect, useRef, useState } from "react";

const GIS_SRC = "https://accounts.google.com/gsi/client";
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

let scriptPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const s = document.createElement("script");
      s.src = GIS_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Nem sikerült betölteni a Google bejelentkezést."));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

interface Props {
  onCredential: (idToken: string) => void;
  disabled?: boolean;
}

/**
 * Google Identity Services gomb. Csak akkor jelenik meg, ha a VITE_GOOGLE_CLIENT_ID
 * be van állítva — így a helyi-fiókos flow enélkül is teljesen működik.
 */
export function GoogleSignInButton({ onCredential, disabled }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!CLIENT_ID || !ref.current) return;
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !ref.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => onCredential(resp.credential),
        });
        window.google.accounts.id.renderButton(ref.current, {
          theme: "outline",
          size: "large",
          shape: "pill",
          text: "continue_with",
          width: 320,
        });
      })
      .catch((e: Error) => setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [onCredential]);

  if (!CLIENT_ID) return null;
  if (error) return <p className="text-center text-sm text-destructive">{error}</p>;

  return <div ref={ref} className={disabled ? "pointer-events-none opacity-50" : ""} aria-label="Bejelentkezés Google-fiókkal" />;
}
